import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { Sparkles, BadgeCheck, ShieldCheck, Truck, RotateCcw } from "lucide-react";

const conditions = [
  {
    name: "Like New",
    grade: "Mint · 10/10",
    icon: Sparkles,
    accent: "text-emerald-600",
    ring: "border-emerald-200 bg-emerald-50",
    cosmetic: "Mint condition — looks and feels indistinguishable from a brand-new device, with no visible signs of use.",
  },
  {
    name: "Excellent",
    grade: "Near Mint · 8/10",
    icon: BadgeCheck,
    accent: "text-crimson",
    ring: "border-line bg-alt",
    cosmetic: "Very minor signs of use — very light scratches, dings, or scuffs that are hard to notice in everyday use.",
  },
  {
    name: "Good",
    grade: "Best value · 7/10",
    icon: ShieldCheck,
    accent: "text-ink",
    ring: "border-line bg-alt",
    cosmetic: "Minor signs of use — light scratches, dings, or blemishes. Purely cosmetic; the device is fully functional.",
  },
];

const promises = [
  { icon: BadgeCheck, title: "PhoneCheck Certified", body: "Every device passes a full PhoneCheck diagnostic and is verified 100% functional before it ships." },
  { icon: ShieldCheck, title: "1-year warranty", body: "Every phone is backed by a 1-year replacement warranty for total peace of mind." },
  { icon: RotateCcw, title: "30-day returns", body: "Not the right fit? You're covered by our 30-day return policy." },
  { icon: Truck, title: "Free UPS shipping", body: "Fast, free shipping on every order — your device arrives ready to use out of the box." },
];

export default function Conditions() {
  return (
    <div className="pt-32" data-testid="conditions-page">
      <section className="mx-auto max-w-7xl px-6 pb-14 lg:px-12">
        <Reveal>
          <span className="text-sm font-bold uppercase tracking-widest text-crimson">Condition guide</span>
          <h1 className="mt-3 max-w-3xl font-display text-5xl font-black leading-none tracking-tighter text-ink sm:text-6xl">
            Know exactly what <span className="text-crimson">you're getting.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Every Adopt-A-Phone device is PhoneCheck Certified and 100% functional — the condition grade only describes the <span className="font-bold text-ink">cosmetic</span> appearance. Here's what to expect from each grade so you can shop with confidence.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-12">
        <div className="grid gap-6 md:grid-cols-3">
          {conditions.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08}>
              <div data-testid={`condition-card-${c.name.replace(/\s+/g, "-").toLowerCase()}`} className="flex h-full flex-col rounded-3xl border border-line bg-white p-8">
                <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${c.ring}`}>
                  <c.icon className={`h-7 w-7 ${c.accent}`} />
                </div>
                <h2 className="mt-5 font-display text-2xl font-black tracking-tight text-ink">{c.name}</h2>
                <div className={`mt-1 text-xs font-bold uppercase tracking-widest ${c.accent}`}>{c.grade}</div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.cosmetic}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-12">
        <Reveal>
          <div className="rounded-3xl border border-line bg-alt p-8 md:p-12">
            <h2 className="font-display text-3xl font-black tracking-tighter text-ink">Every grade, fully backed</h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">No matter which condition you choose, every device comes with the same guarantees.</p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {promises.map((p) => (
                <div key={p.title} className="rounded-2xl bg-white p-6">
                  <p.icon className="h-7 w-7 text-crimson" />
                  <h3 className="mt-4 font-display text-lg font-bold text-ink">{p.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-12">
        <Reveal>
          <div className="flex flex-col items-start gap-6 rounded-3xl bg-surface-dark p-10 text-white md:flex-row md:items-center md:justify-between md:p-14">
            <div>
              <h2 className="font-display text-3xl font-black tracking-tighter">Ready to find yours?</h2>
              <p className="mt-2 text-white/60">Browse certified devices and pick the condition that fits your budget.</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Link to="/products" data-testid="conditions-shop-btn" className="inline-flex items-center justify-center rounded-full bg-crimson px-8 py-4 font-bold text-white transition-colors hover:bg-crimson-hover">
                Shop iPhones
              </Link>
              <Link to="/ipads" data-testid="conditions-shop-ipads-btn" className="inline-flex items-center justify-center rounded-full bg-crimson px-8 py-4 font-bold text-white transition-colors hover:bg-crimson-hover">
                Shop iPads
              </Link>
              <Link to="/macbooks" data-testid="conditions-shop-macbooks-btn" className="inline-flex items-center justify-center rounded-full bg-crimson px-8 py-4 font-bold text-white transition-colors hover:bg-crimson-hover">
                Shop MacBooks
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
